import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { tokenStorage } from '@utils/tokenStorage';
import { USER_ACCESS_TOKEN } from '@constants/auth';

// Django scraper backend types
export interface ScrapeJob {
  id: string;
  url: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  schema: Record<string, any>;
  use_javascript: boolean;
  follow_pagination: boolean;
  max_pages: number;
  total_items: number;
  items_scraped: number;
  error_message?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  results_count: number;
}

export interface Campaign {
  id: string;
  name: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'failed';
  campaign_type: string;
  config: Record<string, any>;
  filters: Record<string, any>;
  schedule_type: 'once' | 'daily' | 'weekly' | 'monthly' | 'cron';
  schedule_config: Record<string, any>;
  reserved_tokens: number;
  used_tokens: number;
  total_leads: number;
  unique_leads: number;
  leads_count: number;
  jobs_count: number;
  email_integration?: Record<string, any>;
  created_at: string;
  updated_at: string;
  metrics?: {
    replies?: number;
    sent?: number;
    opened?: number;
    clicked?: number;
  };
}

export interface CampaignStatus {
  scraper: {
    id: string;
    name: string;
    status: string;
    leads_collected: number;
    jobs_completed: number;
    jobs_failed: number;
    tokens_used: number;
    created_at: string;
  };
  email?: {
    id: string;
    status: string;
    emails_sent: number;
    emails_opened: number;
    emails_replied: number;
    leads_processed: number;
    leads_pending: number;
  };
  integration?: Record<string, any>;
}

export interface EmailLinkRequest {
  email_campaign_id: string;
  flow_config?: {
    enabled?: boolean;
    batch_size?: number;
    delay_minutes?: number;
    quality_threshold?: number;
    deduplication?: boolean;
  };
}

export interface FlowConfigRequest {
  enabled?: boolean;
  batch_size?: number;
  delay_minutes?: number;
  quality_threshold?: number;
  deduplication?: boolean;
}

export interface Lead {
  id: string;
  campaign: string;
  data: Record<string, any>;
  quality_score: number;
  is_public: boolean;
  price_tokens?: number;
  created_at: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  user_count: number;
  created_at: string;
  updated_at: string;
}

export interface ScrapingJob {
  id: string;
  campaign?: string;
  campaign_name?: string;
  url: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  engine: 'scrapy_spider' | 'playwright_flow' | 'ai_extraction' | 'visual_flow' | 'aws_batch';
  items_scraped: number;
  tokens_used: number;
  error_message?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

// Queue status (CampaignDetails widget — Phase 1)
export interface CampaignQueueStatus {
  campaign_id: string;
  /** 1-based queue position; 0 if no pending tasks. */
  position: number;
  total_pending: number;
  slots_in_use: number;
  slots_total: number;
  eta_seconds: number;
  /** Canonical tier id (free, starter, growth, professional, scale, enterprise). */
  tier: string;
  /** Lowest tier where VIP routing kicks in (e.g. 'scale'); used for upgrade CTA copy. */
  priority_unlocked_at_tier: string | null;
  /** True if the org is already on a VIP tier. */
  priority_currently_active: boolean;
}

// Email Preview types
export interface EmailSequence {
  id: string;
  subject: string;
  body: string;
  delayDays: number;
  order: number;
}

export interface EmailPreviewLead {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  city: string;
  state: string;
  industry: string;
  phone: string;
  website?: string;
  isBlocked?: boolean;
  isUnsubscribed?: boolean;
}

export interface EmailPreviewResponse {
  sequences: EmailSequence[];
  leads: EmailPreviewLead[];
  total: number;
  page: number;
  page_size: number;
  email_campaign_id: string;
  stats?: {
    ready: number;
    missing_fields: number;
    no_email: number;
  };
  valid_lead_ids?: string[];
}

export interface AuthorizeSendingResponse {
  success: boolean;
  scheduledCount: number;
  totalValidLeads: number;
  message: string;
}

// Contact Form types
export interface ContactFormCampaignData {
  id: string;
  name: string;
  status: string;
  total_leads: number;
  leads_with_forms: number;
  submissions_attempted: number;
  submissions_successful: number;
  submissions_failed: number;
  submissions_blocked: number;
  scraping_campaign: string;
}

export interface ContactFormCampaignStats {
  exists: boolean;
  total_leads: number;
  leads_with_forms: number;
  submissions_attempted: number;
  submissions_successful: number;
  submissions_failed: number;
  submissions_blocked: number;
  status: string;
  campaign_id: string | null;
}

// Create the scraper API slice
export const scraperApi = createApi({
  reducerPath: 'scraperApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_SCRAPER_API_URL || 'http://localhost:8000/api',
    prepareHeaders: headers => {
      const token = tokenStorage.getToken(USER_ACCESS_TOKEN);
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Campaign', 'ScrapeJob', 'Lead', 'Organization', 'ScrapingJob'],
  endpoints: builder => ({
    // Campaigns
    getCampaigns: builder.query<Campaign[], void>({
      query: () => '/campaigns/?limit=1000',
      transformResponse: (response: any) =>
        Array.isArray(response) ? response : response?.data || response?.results || [],
      providesTags: ['Campaign'],
    }),

    getCampaign: builder.query<Campaign, string>({
      query: id => `/campaigns/${id}/`,
      providesTags: ['Campaign'],
    }),

    createCampaign: builder.mutation<Campaign, Partial<Campaign>>({
      query: data => ({
        url: '/campaigns/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Campaign'],
    }),

    updateCampaign: builder.mutation<Campaign, { id: string; data: Partial<Campaign> }>({
      query: ({ id, data }) => ({
        url: `/campaigns/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Campaign'],
    }),

    deleteCampaign: builder.mutation<void, string>({
      query: id => ({
        url: `/campaigns/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Campaign'],
    }),

    // Campaign Actions
    startCampaign: builder.mutation<void, string>({
      query: id => ({
        url: `/campaigns/${id}/start/`,
        method: 'POST',
      }),
      invalidatesTags: ['Campaign'],
    }),

    stopCampaign: builder.mutation<void, string>({
      query: id => ({
        url: `/campaigns/${id}/stop/`,
        method: 'POST',
      }),
      invalidatesTags: ['Campaign'],
    }),

    // Email Integration Endpoints
    linkCampaignToEmail: builder.mutation<void, { id: string; data: EmailLinkRequest }>({
      query: ({ id, data }) => ({
        url: `/campaigns/${id}/link_email/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Campaign'],
    }),

    getCampaignStatus: builder.query<CampaignStatus, string>({
      query: id => `/campaigns/${id}/status/`,
      providesTags: ['Campaign'],
    }),

    updateFlowConfig: builder.mutation<void, { id: string; data: FlowConfigRequest }>({
      query: ({ id, data }) => ({
        url: `/campaigns/${id}/flow_config/`,
        method: 'POST',
        body: { flow_config: data },
      }),
      invalidatesTags: ['Campaign'],
    }),

    // Campaign Leads
    getCampaignLeads: builder.query<
      { leads: Lead[]; pagination: any },
      {
        id: string;
        page?: number;
        per_page?: number;
        quality_min?: number;
      }
    >({
      query: ({ id, ...params }) => ({
        url: `/campaigns/${id}/leads/`,
        params,
      }),
      providesTags: ['Lead'],
    }),

    // Legacy Scrape Jobs
    getScrapeJobs: builder.query<ScrapeJob[], void>({
      query: () => '/jobs/',
      providesTags: ['ScrapeJob'],
    }),

    createScrapeJob: builder.mutation<ScrapeJob, Partial<ScrapeJob>>({
      query: data => ({
        url: '/jobs/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ScrapeJob'],
    }),

    getScrapeJob: builder.query<ScrapeJob, string>({
      query: id => `/jobs/${id}/`,
      providesTags: ['ScrapeJob'],
    }),

    // Enhanced Monitoring
    getScrapingJobs: builder.query<
      ScrapingJob[],
      {
        status?: string;
        campaign?: string;
      }
    >({
      query: params => ({
        url: '/monitoring/',
        params,
      }),
      providesTags: ['ScrapingJob'],
    }),

    getScrapingJob: builder.query<ScrapingJob, string>({
      query: id => `/monitoring/${id}/`,
      providesTags: ['ScrapingJob'],
    }),

    cancelScrapingJob: builder.mutation<void, string>({
      query: id => ({
        url: `/monitoring/${id}/cancel/`,
        method: 'POST',
      }),
      invalidatesTags: ['ScrapingJob'],
    }),

    getJobStatistics: builder.query<any, void>({
      query: () => '/monitoring/statistics/',
    }),

    // Organizations
    getOrganizations: builder.query<Organization[], void>({
      query: () => '/organizations/',
      providesTags: ['Organization'],
    }),

    createOrganization: builder.mutation<Organization, { name: string; slug: string }>({
      query: data => ({
        url: '/organizations/create_organization/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Organization'],
    }),

    getOrganizationStatistics: builder.query<any, string>({
      query: id => `/organizations/${id}/statistics/`,
    }),

    // Email Preview for lead review
    getEmailPreview: builder.query<EmailPreviewResponse, { campaignId: string; page?: number; pageSize?: number }>({
      query: ({ campaignId, page = 1, pageSize = 25 }) =>
        `/campaigns/${campaignId}/email-preview/?page=${page}&page_size=${pageSize}`,
      providesTags: ['Campaign'],
    }),

    // Update lead data (for fixing shortcodes)
    updateLeadData: builder.mutation<
      { success: boolean },
      { campaignId: string; leadId: string; data: Record<string, string> }
    >({
      query: ({ campaignId, leadId, data }) => ({
        url: `/campaigns/${campaignId}/leads/${leadId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Campaign'],
    }),

    // Authorize email sending
    authorizeSending: builder.mutation<AuthorizeSendingResponse, { campaignId: string; leadIds: string[] }>({
      query: ({ campaignId, leadIds }) => ({
        url: `/campaigns/${campaignId}/authorize-sending`,
        method: 'POST',
        body: { leadIds },
      }),
      invalidatesTags: ['Campaign'],
    }),

    // Update email sequences
    updateEmailSequences: builder.mutation<
      {
        success: boolean;
        sequences: Array<{ step_number: number; delay_days: number; subject: string; body: string }>;
      },
      {
        campaignId: string;
        sequences: Array<{ step_number: number; delay_days: number; subject: string; body: string }>;
      }
    >({
      query: ({ campaignId, sequences }) => ({
        url: `/campaigns/${campaignId}/email-sequences/`,
        method: 'PATCH',
        body: { sequences },
      }),
      invalidatesTags: ['Campaign'],
    }),

    // Start contact form outreach from campaign config
    startContactFormOutreach: builder.mutation<{ id: string; status: string; message: string }, string>({
      query: campaignId => ({
        url: `/campaigns/${campaignId}/contact-form-start/`,
        method: 'POST',
      }),
      invalidatesTags: ['Campaign'],
    }),

    // Get contact form status for a campaign
    getContactFormStatus: builder.query<
      {
        exists: boolean;
        id?: string;
        status?: string;
        total_leads?: number;
        leads_with_forms?: number;
        submissions_attempted?: number;
        submissions_successful?: number;
        submissions_failed?: number;
      },
      string
    >({
      query: campaignId => `/campaigns/${campaignId}/contact-form-status/`,
      providesTags: ['Campaign'],
    }),

    // Contact Form Stats for a scraping campaign
    getContactFormStats: builder.query<ContactFormCampaignStats, string>({
      query: campaignId => `/contact-forms/campaigns/?scraping_campaign=${campaignId}`,
      transformResponse: (response: ContactFormCampaignData[]) => {
        // Aggregate stats from all contact form campaigns for this scraping campaign
        if (!response || response.length === 0) {
          return {
            exists: false,
            total_leads: 0,
            leads_with_forms: 0,
            submissions_attempted: 0,
            submissions_successful: 0,
            submissions_failed: 0,
            submissions_blocked: 0,
            status: 'none',
            campaign_id: null,
          };
        }
        const primary = response[0];
        return {
          exists: true,
          total_leads: primary.total_leads || 0,
          leads_with_forms: primary.leads_with_forms || 0,
          submissions_attempted: primary.submissions_attempted || 0,
          submissions_successful: primary.submissions_successful || 0,
          submissions_failed: primary.submissions_failed || 0,
          submissions_blocked: primary.submissions_blocked || 0,
          status: primary.status,
          campaign_id: primary.id,
        };
      },
      providesTags: ['Campaign'],
    }),

    // Health check
    healthCheck: builder.query<{ status: string }, void>({
      query: () => '/health/',
    }),

    // Templates
    getScrapeTemplates: builder.query<any[], void>({
      query: () => '/templates/',
    }),

    // Hybrid Scraping
    getSpiders: builder.query<any[], void>({
      query: () => '/hybrid/spiders/',
    }),

    startHybridScrape: builder.mutation<any, any>({
      query: data => ({
        url: '/hybrid/scrape/',
        method: 'POST',
        body: data,
      }),
    }),

    getEngineStats: builder.query<any, void>({
      query: () => '/hybrid/stats/',
    }),

    // Enrichment progress for a campaign
    getEnrichmentProgress: builder.query<
      { total: number; enriched: number; pending: number; percentage: number },
      string
    >({
      query: campaignId => `/campaigns/${campaignId}/enrichment-progress/`,
    }),

    // Queue status for a campaign (Phase 1: position + ETA + upgrade CTA data)
    getCampaignQueueStatus: builder.query<CampaignQueueStatus, string>({
      query: campaignId => `/scrapers/campaigns/${campaignId}/queue-status/`,
      providesTags: ['Campaign'],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  // Campaigns
  useGetCampaignsQuery,
  useGetCampaignQuery,
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useDeleteCampaignMutation,

  // Campaign Actions
  useStartCampaignMutation,
  useStopCampaignMutation,

  // Email Integration
  useLinkCampaignToEmailMutation,
  useGetCampaignStatusQuery,
  useUpdateFlowConfigMutation,

  // Email Preview & Review
  useGetEmailPreviewQuery,
  useUpdateLeadDataMutation,
  useAuthorizeSendingMutation,

  // Email Sequence Editing
  useUpdateEmailSequencesMutation,

  // Contact Forms
  useStartContactFormOutreachMutation,
  useGetContactFormStatusQuery,
  useGetContactFormStatsQuery,

  // Campaign Leads
  useGetCampaignLeadsQuery,

  // Legacy Jobs
  useGetScrapeJobsQuery,
  useCreateScrapeJobMutation,
  useGetScrapeJobQuery,

  // Enhanced Monitoring
  useGetScrapingJobsQuery,
  useGetScrapingJobQuery,
  useCancelScrapingJobMutation,
  useGetJobStatisticsQuery,

  // Organizations
  useGetOrganizationsQuery,
  useCreateOrganizationMutation,
  useGetOrganizationStatisticsQuery,

  // Utilities
  useHealthCheckQuery,
  useGetScrapeTemplatesQuery,
  useGetSpidersQuery,
  useStartHybridScrapeMutation,
  useGetEngineStatsQuery,
  useGetEnrichmentProgressQuery,

  // Queue status (Phase 1)
  useGetCampaignQueueStatusQuery,
} = scraperApi;
